/* eslint-disable @typescript-eslint/no-explicit-any */

import { Component, DestroyRef, ViewEncapsulation } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { RoutingService } from '../../../service/routing.service';
import { FeatureFlagsService } from '../../../shared/services/customer-api/feature-flags.service';
import { PortfolioApiService } from '../../../shared/services/customer-api/portfolio-api.service';
import { NewsFeedService } from './news-feed/news-feed.service';
import { ConfigService } from 'src/app/service/config.service';
import { StoriesService } from 'src/app/shared/services/data-api/stories.service';
import { NewsService } from 'src/app/shared/services/data-api/news.service';
import { TopNewsService } from 'src/app/shared/services/data-api/top-news.service';
import {
  NewsfeedOptions,
  NewsfeedParams,
  NewsfeedResponse,
  NewsfeedData,
} from './news-newsfeed-interface/news-feed.interfaces';
import {
  MainNewsfeedREFRESH_PARAMS,
  NewsType,
} from './news-newsfeed-interface/news-type.constant';

@Component({
  encapsulation: ViewEncapsulation.None,
  selector: 'app-news-newsfeed',
  templateUrl: './news-newsfeed.component.html',
  styleUrls: ['./news-newsfeed.component.scss'],
})
export class MainNewsfeedComponent {
  newsfeedDataFeed: NewsfeedData = {};
  newsfeedDataCount: NewsfeedData = {};
  params: NewsfeedParams = {};
  previousValue: NewsfeedParams = {};
  newstype: string = '';
  project: any;
  private static readonly REFRESH_PARAMS = MainNewsfeedREFRESH_PARAMS;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private newsFeedService: NewsFeedService,
    public routing: RoutingService,
    public config: ConfigService,
    private storiesService: StoriesService,
    private newsService: NewsService,
    private topNewsService: TopNewsService,
    private featureFlagsService: FeatureFlagsService,
    private portfolioApi: PortfolioApiService,
    private destroyRef: DestroyRef,
  ) {
    this.initializeRouteSubscription();
  }
  private initializeRouteSubscription(): void {
    // Without takeUntilDestroyed every destroyed instance keeps refetching the
    // feed on each query-param write.
    this.route.queryParams
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((params) => {
        this.handleQueryParams(params);
      });
  }
  private handleQueryParams(params: NewsfeedParams): void {
    this.params = params;

    if (this.shouldFetchNewsfeed(params)) {
      this.newstype = this.params.newstype || NewsType.CHRONOLOGICAL;
      this.fetchNewsfeedData();
    }
    this.previousValue = params;
  }

  private shouldFetchNewsfeed(params: NewsfeedParams): boolean {
    return (
      !this.isEventsMissingContext(params) &&
      (params.id || params.isin || this.routing.isHot()) &&
      this.routing.reFresh(
        this.withDefaultNewstype(params),
        this.withDefaultNewstype(this.previousValue),
        MainNewsfeedComponent.REFRESH_PARAMS,
      )
    );
  }

  // NewsCountComponent spells out the implicit newstype in its own navigation.
  // Without this the feed refetches an identical payload for that write alone.
  private withDefaultNewstype(params: NewsfeedParams): NewsfeedParams {
    return { ...params, newstype: params.newstype || NewsType.CHRONOLOGICAL };
  }

  private isEventsMissingContext(params: NewsfeedParams): boolean {
    if (params.newstype !== 'Events') return false;
    if (!params.eventid) return true;
    if (this.routing.isScanner() && !params.tag) return true;
    return false;
  }

  private readonly newsFeedHandlers = new Map<
    NewsType | string,
    (handleOnlyFeed: boolean) => void
  >([
    [NewsType.RELEVANT, () => this.fetchTopNewsNewsfeed()],
    [NewsType.STORIES, () => this.fetchStories()],
    [
      NewsType.CHRONOLOGICAL,
      (handleOnlyFeed) => this.fetchChronologicalAllNewdata(handleOnlyFeed),
    ],
    [
      NewsType.BOOKMARKED,
      (handleOnlyFeed) => this.fetchBookMarkedNewsData(handleOnlyFeed),
    ],
    [
      NewsType.FLAGGED,
      (handleOnlyFeed) => this.fetchFlaggedNewsData(handleOnlyFeed),
    ],
    [
      NewsType.DISMISSED,
      (handleOnlyFeed) => this.fetchDismissedNewsData(handleOnlyFeed),
    ],
    [NewsType.EVENTS, () => this.fetchEventSnippetsNewsData()],
    ['default', () => this.fetchEventSnippetsNewsData()],
  ]);

  private fetchNewsfeedData(): void {
    // A portfolio without entities cannot produce a valid news request
    // (the API 422s without sentiment_filter.entities) — skip the fetch
    if (this.isEmptyPortfolioContext()) {
      this.newsfeedDataFeed = { emptyPortfolio: true };
      this.newsfeedDataCount = { emptyPortfolio: true };
      return;
    }

    const sentimentFilterExists = !!(
      this.params.filter &&
      Array.isArray(this.params.filter) &&
      this.params.filter.length > 0
    );

    // The unfiltered sentiment counts feed only the news-count pie. Fetch them as a separate
    // source ONLY when that pie will actually render — otherwise the request is wasted. When we
    // do, the main feed request populates only the list (handleOnlyFeed); when we don't, the
    // single feed request populates both the list and the counts.
    const fetchCounts =
      sentimentFilterExists && this.shouldFetchSentimentCounts();

    if (fetchCounts) {
      this.newsService
        .getAllNews({
          customParams: {
            size: 1,
          },
          body: {
            type: this.setNewsContentTypeBasedOnParams(),
            skipSentimentFilter: true,
          },
        })
        .subscribe({
          next: (result) => this.handleCountSuccess(result),
          error: () => this.handleCountError(),
        });
    }
    const handler =
      this.newsFeedHandlers.get(this.newstype as NewsType) ||
      this.newsFeedHandlers.get('default');

    handler?.(fetchCounts);
  }

  private isEmptyPortfolioContext(): boolean {
    return (
      this.routing.isFolio() &&
      this.setNewsContentTypeBasedOnParams() === 'entities' &&
      this.portfolioApi.activePortfolioContent.length === 0
    );
  }

  private setNewsContentTypeBasedOnParams(): string {
    if (this.routing.isQuery() || this.routing.isSearch()) {
      return 'search_query';
    } else {
      return 'entities';
    }
  }

  private shouldFetchSentimentCounts(): boolean {
    return !!(
      this.featureFlagsService.showArticleSentiment &&
      !this.routing.isScanner() &&
      ![NewsType.RELEVANT, NewsType.STORIES, NewsType.EVENTS].includes(
        this.newstype as NewsType,
      )
    );
  }

  private fetchChronologicalAllNewdata(handleOnlyFeed): void {
    this.newsService
      .getAllNews({
        customParams: {
          size: this.params.size || 50,
        },
        body: { type: this.setNewsContentTypeBasedOnParams() },
      })
      .subscribe({
        next: (result) => {
          if (handleOnlyFeed) {
            this.handleFeedSuccess(result);
          } else {
            this.handleBothSuccess(result);
          }
        },
        error: () => this.handleError(),
      });
  }

  private fetchBookMarkedNewsData(handleOnlyFeed): void {
    this.newsService
      .getBookMarkedNews({
        customParams: {
          size: this.params.size || 50,
        },
        body: { type: this.setNewsContentTypeBasedOnParams() },
      })
      .subscribe({
        next: (result) => {
          if (handleOnlyFeed) {
            this.handleFeedSuccess(result);
          } else {
            this.handleBothSuccess(result);
          }
        },
        error: () => this.handleError(),
      });
  }

  private fetchFlaggedNewsData(handleOnlyFeed): void {
    this.newsService
      .getFlaggedNews({
        customParams: {
          size: this.params.size || 50,
        },
        body: { type: this.setNewsContentTypeBasedOnParams() },
      })
      .subscribe({
        next: (result) => {
          if (handleOnlyFeed) {
            this.handleFeedSuccess(result);
          } else {
            this.handleBothSuccess(result);
          }
        },
        error: () => this.handleError(),
      });
  }

  private fetchDismissedNewsData(handleOnlyFeed): void {
    this.newsService
      .getDismissedNews({
        customParams: {
          size: this.params.size || 50,
        },
        body: { type: this.setNewsContentTypeBasedOnParams() },
      })
      .subscribe({
        next: (result) => {
          if (handleOnlyFeed) {
            this.handleFeedSuccess(result);
          } else {
            this.handleBothSuccess(result);
          }
        },
        error: () => this.handleError(),
      });
  }

  private fetchStories(): void {
    this.storiesService
      .getStories({
        customParams: {
          size: this.params.size || 100,
        },
        body: { type: this.setNewsContentTypeBasedOnParams() },
      })
      .subscribe({
        next: this.handleStoriesSuccess.bind(this),
        error: this.handleError.bind(this),
      });
  }

  private fetchTopNewsNewsfeed(): void {
    this.topNewsService
      .getTopNews({
        customParams: {
          size: this.params.size || 100,
        },
        body: { type: this.setNewsContentTypeBasedOnParams() },
      })
      .subscribe({
        next: this.handleStoriesSuccess.bind(this),
        error: this.handleError.bind(this),
      });
  }

  private fetchEventSnippetsNewsData(): void {
    this.newsFeedService
      .newsFeed(
        this.createNewsfeedOptions(this.newstype !== 'Events' ? 50 : 1000),
      )
      .subscribe({
        next: (result) => {
          this.handleBothSuccess(result);
        },
        error: () => this.handleError(),
      });
  }

  private createNewsfeedOptions(size: number): NewsfeedOptions {
    return {
      newsType: this.newstype as NewsType,
      returnEvents: this.newstype === 'Events',
      params: this.params,
      custom: {
        next: null,
        size,
        starred_only: this.newstype === 'Bookmarked',
        audited_only: this.newstype === 'Flagged',
        dismissed_only: this.newstype === 'Dismissed',
        top_news_score_threshold: false,
        license: false,
        title: false,
      },
    };
  }

  private handleStoriesSuccess(result: NewsfeedResponse): void {
    const data: NewsfeedData = result.stories
      ? { ...result, reqError: false }
      : {
          // If result is a stories array directly
          stories: Array.isArray(result) ? result : [],
          reqError: false,
        };
    this.newsfeedDataFeed = data;
    this.newsfeedDataCount = data;

    this.finalizeSuccess();
  }

  // Unfiltered sentiment counts (skipSentimentFilter) — feeds only the news-count pie.
  private handleCountSuccess(result: NewsfeedResponse): void {
    this.newsfeedDataCount = { ...result, reqError: false };
  }

  // Filtered article list — feeds only the news-feed list.
  private handleFeedSuccess(result: NewsfeedResponse): void {
    this.newsfeedDataFeed = { ...result, reqError: false };
  }

  // No separate count request: this one response feeds both the list and the counts.
  private handleBothSuccess(result: NewsfeedResponse): void {
    const data: NewsfeedData = { ...result, reqError: false };
    this.newsfeedDataFeed = data;
    this.newsfeedDataCount = data;
  }

  private handleCountError(): void {
    this.newsfeedDataCount = { ...this.newsfeedDataCount, reqError: true };
  }

  private handleError(): void {
    this.newsfeedDataFeed = { reqError: true };
    this.newsfeedDataCount = { reqError: true };
  }

  private finalizeSuccess(): void {
    this.newsfeedDataFeed = {
      ...this.newsfeedDataFeed,
      reqError: false,
      tabsEventsFromScanner: this.params.tabsEventsFromScanner || undefined,
    };

    this.router.navigate([], {
      queryParams: { tabsEventsFromScanner: null },
      queryParamsHandling: 'merge',
      replaceUrl: false,
    });
  }
}
